import { useId, useRef } from 'react';
import type { ReactNode } from 'react';
import { Box, Heading, Paragraph, Tag } from '@zillow/constellation-10';
import type { Document, RequiredDocumentSection } from '../../../../../models/rental-application';
import { usePendingPickActions } from '../../../../../hooks/usePendingDocumentPicks';
import { usePendingRemovalActions } from '../../../../../hooks/usePendingDocumentRemovals';
import type { DocumentDetailsStatus } from '../../../../../hooks/useStepDocumentDetails';
import { useStepDocumentDetailsContext } from '../../../../../hooks/useStepDocumentDetails';
import { DocumentCardSkeleton } from './DocumentCardSkeleton';
import type { FailedUploads } from './FailedUploadCard';
import { FailedUploadCard } from './FailedUploadCard';
import { DocumentCard } from './DocumentCard';
import { DocumentCardMenu } from './DocumentCardMenu';

export type DocumentRemoval = {
  label: string;
  section: RequiredDocumentSection;
};

type CardActions = {
  removal?: DocumentRemoval;
  canDelete: boolean;
};

type Props = CardActions & {
  heading: string;
  tag?: string;
  requirement?: string;
  isRequested?: boolean;
  documents: Array<Document>;
  action?: ReactNode;
  failed?: FailedUploads;
  picker?: (actions: { focusHeading: () => void }) => ReactNode;
};

function toShownDocument(document: Document, status: DocumentDetailsStatus, hasPollEnded: boolean): Document {
  // capps returns only unexpired files, so a file that an answer left out has expired
  if (status === 'inactive') {
    return { ...document, expirationDateTime: new Date(0).toISOString() };
  }
  // When the poll ends with no redaction result, the card shows the original preview
  if (hasPollEnded && !document.isRedactionCheckComplete && document.previewUrl) {
    return { ...document, isRedactionCheckComplete: true, canBeRedacted: false };
  }
  return document;
}

function StepDocumentCard({ document: answerDocument, removal, canDelete }: CardActions & { document: Document }) {
  const { detach, hideDeleted, isDetaching } = usePendingRemovalActions();
  const details = useStepDocumentDetailsContext();
  const { isPicking } = usePendingPickActions();
  const { id: documentId } = answerDocument;
  const status = documentId ? details.statusOf(documentId) : 'ready';
  const document = toShownDocument(
    (documentId && details.detailOf(documentId)) || answerDocument,
    status,
    !!documentId && details.hasPollEnded(documentId),
  );
  const { documentType, incomeId } = removal?.section ?? {};

  const onRemove =
    documentId && documentType
      ? () => void detach({ documentId, documentType, incomeId: incomeId ?? undefined })
      : undefined;

  // An error keeps the card with its spinner and an enabled menu, so the renter can still Remove or Delete
  if (status === 'loading') {
    return <DocumentCardSkeleton />;
  }

  return (
    <DocumentCard
      document={document}
      onDeleted={hideDeleted}
      deleteConfirmationText="This also deletes it from all applications you have not submitted."
      menu={({ onDelete }) => (
        <DocumentCardMenu
          fileName={document.fileName}
          removeLabel={removal?.label}
          onRemove={onRemove}
          onDelete={canDelete ? onDelete : undefined}
          disabled={!documentId || isDetaching(documentId) || isPicking(documentId)}
        />
      )}
    />
  );
}

export function DocumentContainer({
  heading,
  tag,
  requirement,
  isRequested,
  documents,
  removal,
  canDelete,
  action,
  failed,
  picker,
}: Props) {
  const headingId = useId();
  const headingRef = useRef<HTMLHeadingElement>(null);

  return (
    // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- a list of file cards, not form controls for a fieldset
    <Box role="group" aria-labelledby={headingId} css={{ display: 'flex', flexDirection: 'column', gap: 'tighter' }}>
      <Box css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'default' }}>
        <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tighter', minWidth: 0 }}>
          <Box css={{ display: 'flex', alignItems: 'center', gap: 'tighter', flexWrap: 'wrap' }}>
            <Heading ref={headingRef} id={headingId} tabIndex={-1} level={4} textStyle="body-lg-bold">
              {heading}
            </Heading>
            {tag && (
              <Tag size="sm" css={{ maxWidth: '100%', overflowWrap: 'anywhere' }}>
                {tag}
              </Tag>
            )}
          </Box>
          {requirement && (
            <Paragraph textStyle="body-sm-bold" color="text.subtle">
              {requirement}
            </Paragraph>
          )}
          {isRequested && (
            <Paragraph textStyle="body-sm" color="text.subtle">
              Date coverage is not verified
            </Paragraph>
          )}
        </Box>
        {action}
      </Box>
      {picker?.({ focusHeading: () => headingRef.current?.focus() })}
      {documents.length + (failed?.uploads.length ?? 0) > 0 ? (
        <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 'tighter' }}>
          {documents.map((document, index) => (
            <Box
              key={document.id ?? `pending-${index}`}
              css={{ gridColumnEnd: { base: 'span 12', md: 'span 6', lg: 'span 4' } }}
            >
              <StepDocumentCard document={document} removal={removal} canDelete={canDelete} />
            </Box>
          ))}
          {failed?.uploads.map((upload) => (
            <Box key={upload.key} css={{ gridColumnEnd: { base: 'span 12', md: 'span 6', lg: 'span 4' } }}>
              <FailedUploadCard upload={upload} onRetry={failed.onRetry} onRemove={failed.onRemove} />
            </Box>
          ))}
        </Box>
      ) : (
        <Paragraph textStyle="body-sm" color="text.subtle">
          No files added
        </Paragraph>
      )}
    </Box>
  );
}
